import {
  Box,
  AppShell,
  Text,
  UnstyledButton,
  AppShellSection,
  Popover,
  Group,
  Flex,
  NavLink,
  ThemeIcon,
} from "@mantine/core";
import { JasperWordmark } from "@jasper/forms";
import { useState } from "react";
import {
  IconHelp,
  IconHome,
  IconLogout,
  IconNotes,
  IconUser,
  IconAdjustments,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { JASPER_HOTLINE_PHONE_NUMBER, LayoutDrawerProps } from ".";

type SideBarMenuItem = {
  name: string;
  icon: React.ReactNode;
  linkTo: string;
  color: string;
};

const upperSideBarMenuItems: SideBarMenuItem[] = [
  {
    name: "Accueil",
    icon: <IconHome />,
    linkTo: "/",
    color: "primary",
  },
  {
    name: "Commandes",
    icon: <IconNotes />,
    linkTo: "/orders",
    color: "primary",
  },
];

const lowerSideBarMenuItems: SideBarMenuItem[] = [
  {
    name: "Compte",
    icon: <IconUser />,
    linkTo: "/settings",
    color: "primary",
  },
  {
    name: "Préférences",
    icon: <IconAdjustments />,
    linkTo: "/settings?tab=ProductPreferences",
    color: "primary",
  },
  {
    name: "Hotline",
    icon: <IconHelp />,
    linkTo: "hotline",
    color: "primary",
  },
  {
    name: "Se Déconnecter",
    icon: <IconLogout />,
    linkTo: "/logout",
    color: "primary",
  },
];

export const LayoutDrawerV1 = ({ onLinkClick }: LayoutDrawerProps) => {
  const navigate = useNavigate();
  return (
    <AppShell.Navbar
      style={theme => ({
        backgroundColor: theme.colors.primaryLight[9],
      })}
      p="sm"
    >
      <AppShellSection>
        <Flex direction="column" align="center">
          <Box mb="md" mt="sm">
            <UnstyledButton
              onClick={() => navigate("/")}
              display="block"
              style={{ cursor: "pointer" }}
            >
              <JasperWordmark size={30} style={{ display: "block" }} />
            </UnstyledButton>
            {import.meta.env.MODE !== "production" && (
              <Text size="sm" ta="center" mt={4}>
                {import.meta.env.MODE}
              </Text>
            )}
          </Box>
        </Flex>
      </AppShellSection>
      <AppShellSection mt="lg" px="md" grow>
        <UpperLinks onLinkClick={onLinkClick} />
      </AppShellSection>
      <AppShellSection px="md">
        <LowerLinks onLinkClick={onLinkClick} />
      </AppShellSection>
    </AppShell.Navbar>
  );
};

interface MainLinkProps extends SideBarMenuItem {
  onLinkClick?: () => void;
  onHotlineClick?: () => void;
}

function MainLink({
  name,
  icon,
  linkTo,
  onLinkClick,
  onHotlineClick,
}: MainLinkProps) {
  const { logOut } = useAuthContext();
  const navigate = useNavigate();

  return (
    <NavLink
      label={name}
      leftSection={
        <ThemeIcon variant="transparent" color="gray.6">
          {icon}
        </ThemeIcon>
      }
      fw={500}
      p="sm"
      bdrs="sm"
      styles={{
        root: {
          "--mantine-color-gray-0": "var(--mantine-color-primaryLight-7)",
        },
      }}
      onClick={async () => {
        if (linkTo === "/logout") {
          logOut();
        } else if (linkTo === "hotline" && onHotlineClick) {
          onHotlineClick();
        } else {
          navigate(linkTo);
          onLinkClick?.();
        }
      }}
    />
  );
}

export function UpperLinks({ onLinkClick }: { onLinkClick?: () => void }) {
  return (
    <Box>
      {upperSideBarMenuItems.map(link => (
        <MainLink key={link.name} {...link} onLinkClick={onLinkClick} />
      ))}
    </Box>
  );
}

export function LowerLinks({ onLinkClick }: { onLinkClick?: () => void }) {
  const [hotlinePopoverOpened, setHotlinePopoverOpened] = useState(false);
  const links = lowerSideBarMenuItems.map(link => {
    if (link.name === "Hotline") {
      return (
        <Popover
          key={link.name}
          opened={hotlinePopoverOpened}
          onChange={setHotlinePopoverOpened}
          position="right-start"
          offset={12}
          width={320}
          radius="md"
          shadow="lg"
          withArrow
          withinPortal
        >
          <Popover.Target>
            <Box>
              <MainLink
                {...link}
                onLinkClick={onLinkClick}
                onHotlineClick={() => setHotlinePopoverOpened(o => !o)}
              />
            </Box>
          </Popover.Target>
          <Popover.Dropdown>
            <Box>
              <Group gap="xs" mb="md">
                <Text fw={700} size="sm" c="greyScale.0">
                  Contacter la hotline
                </Text>
              </Group>
              <Text size="sm" c="dimmed" mb="md" lh={1.6}>
                Pour contacter notre équipe de support, veuillez appeler au
                numéro :{" "}
                <Text span fw={700}>
                  {JASPER_HOTLINE_PHONE_NUMBER}
                </Text>
              </Text>
            </Box>
          </Popover.Dropdown>
        </Popover>
      );
    }
    return <MainLink key={link.name} {...link} onLinkClick={onLinkClick} />;
  });
  return (
    <Box>
      <Box>{links}</Box>
      <Text size="xs" ta="center" c="dimmed">
        v{import.meta.env.VITE_APP_VERSION}
      </Text>
    </Box>
  );
}
